// src/components/checkout/addressForm.js

import React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

// Validation schema
const addressSchema = Yup.object().shape({
  fullName: Yup.string()
    .required('Full name is required')
    .min(3, 'Full name must be at least 3 characters'),
  
  phone: Yup.string()
    .required('Phone number is required')
    .matches(/^[6-9]\d{9}$/, 'Please provide a valid Indian mobile number (10 digits starting with 6-9)'),
  
  addressLine1: Yup.string()
    .required('Address line 1 is required')
    .min(5, 'Address must be at least 5 characters'),
  
  city: Yup.string()
    .required('City is required'),
  
  state: Yup.string()
    .required('State is required'),
  
  pincode: Yup.string()
    .required('PIN code is required')
    .matches(/^\d{6}$/, 'Please provide a valid 6-digit Indian PIN code'),
  
  addressType: Yup.string()
    .required('Address type is required')
});

const AddressForm = ({ onSubmit, initialValues = {}, isEdit = false }) => {
  const defaultValues = {
    fullName: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    landmark: '',
    city: '',
    state: '',
    pincode: '',
    addressType: 'HOME',
    isDefault: false,
    ...initialValues
  };
  
  // Indian states list
  const states = [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", 
    "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", 
    "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", 
    "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", 
    "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
    "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu", 
    "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
  ];
  
  return (
    <Formik
      initialValues={defaultValues}
      validationSchema={addressSchema}
      onSubmit={onSubmit}
    >
      {({ isSubmitting }) => (
        <Form className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 mb-1">Full Name*</label>
              <Field
                type="text"
                name="fullName"
                className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your full name"
              />
              <ErrorMessage name="fullName" component="div" className="text-red-500 text-sm mt-1" />
            </div>
            
            <div>
              <label className="block text-gray-700 mb-1">Phone Number*</label>
              <Field
                type="text"
                name="phone"
                className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="10-digit mobile number"
              />
              <ErrorMessage name="phone" component="div" className="text-red-500 text-sm mt-1" />
            </div>
          </div>
          
          <div>
            <label className="block text-gray-700 mb-1">Address Line 1*</label>
            <Field
              type="text"
              name="addressLine1"
              className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="House No, Building Name, Street"
            />
            <ErrorMessage name="addressLine1" component="div" className="text-red-500 text-sm mt-1" />
          </div>
          
          <div>
            <label className="block text-gray-700 mb-1">Address Line 2</label>
            <Field
              type="text"
              name="addressLine2"
              className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Area, Colony (Optional)"
            />
          </div>
          
          <div>
            <label className="block text-gray-700 mb-1">Landmark</label>
            <Field
              type="text"
              name="landmark"
              className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nearby landmark (Optional)"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-gray-700 mb-1">City*</label>
              <Field
                type="text"
                name="city"
                className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter city"
              />
              <ErrorMessage name="city" component="div" className="text-red-500 text-sm mt-1" />
            </div>
            
            <div>
              <label className="block text-gray-700 mb-1">State*</label>
              <Field
                as="select"
                name="state"
                className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select State</option>
                {states.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </Field>
              <ErrorMessage name="state" component="div" className="text-red-500 text-sm mt-1" />
            </div>
            
            <div>
              <label className="block text-gray-700 mb-1">PIN Code*</label>
              <Field
                type="text"
                name="pincode"
                className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="6-digit pincode"
              />
              <ErrorMessage name="pincode" component="div" className="text-red-500 text-sm mt-1" />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 mb-1">Address Type*</label>
              <Field
                as="select"
                name="addressType"
                className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="HOME">Home</option>
                <option value="WORK">Work</option>
                <option value="OTHER">Other</option>
              </Field>
              <ErrorMessage name="addressType" component="div" className="text-red-500 text-sm mt-1" />
            </div>
            
            <div className="flex items-center">
              <Field
                type="checkbox"
                name="isDefault"
                className="mr-2"
              />
              <label className="text-gray-700">Set as default address</label>
            </div>
          </div>
          
          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : isEdit ? 'Update Address' : 'Save Address'}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default AddressForm;